import { Injectable } from '@angular/core';
import {
  ComboGroup,
  CreateGroupRequest,
  CreateMenuSectionRequest, DisplayedGroup,
  DisplayedProduct,
  DisplayedSize,
  Group,
  MenuSection,
  ModifierGroup,
  OverridePositionRequest, OverridePositionsMap,
  PositionTypeEnum,
  Product,
  Size,
  UpdateMenuSectionRequest,
} from 'app/models/menu.model';
import { DishesService } from 'app/services/dishes.service';
import { Store } from '@ngrx/store';
import { MenuOverrideService } from 'app/services/menu-override.service';
import {
  selectDictionaryGroupsAndOverridePositions,
  selectInfoForOverridePositions,
  selectMenuOrganization,
  selectMenuSections,
} from 'app/reducers/menu.selectors';
import { tap } from 'rxjs/operators';
import * as MenuActions from 'app/reducers/menu.actions';
import {
  setMenuSyncOfPOS,
  setMenuSyncOfSales
} from 'app/reducers/menu.actions';
import { updateObject } from 'app/shared/utils/updateObject';
import { concatLatestFrom } from '@ngrx/effects';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IBaseResponse } from 'app/models/baseResponse.model';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  constructor(
    private dishesService: DishesService,
    private store: Store,
    private translate: TranslateService,
  ) {
  }

  public async saveOverridePositions(
    currentMenuSectionId: string,
    overridePositionsRequest: OverridePositionRequest[],
    menuSectionIds: string[])
    :Promise<void>
  {
    let { overridePositionsMap, originalDictionaryGroups,
      originalDictionaryProducts, originalDictionarySizes,
      originalDictionaryModifierGroup, originalDictionaryComboGroup }
      = await firstValueFrom(this.store.select(selectInfoForOverridePositions));

    const response = await this.dishesService.saveOverridePositions(overridePositionsRequest, menuSectionIds);
    const responseOverridePositions = response.data ?? [];

    if (!responseOverridePositions.length) {
      window.alert('not success'); // TODO: обработать этот момент везде, где используется метод saveOverridePositions
    }

    const responsePositionTypes = new Set<PositionTypeEnum>();
    for (const overridePosition of responseOverridePositions) {
      responsePositionTypes.add(overridePosition.positionType);
      // TODO: возможно лучше если сервер будет сразу возвращать overrides только для текущей секции меню
      if (overridePosition.menuSectionId !== currentMenuSectionId){
        continue;
      }
      overridePositionsMap.addOrUpdateOverride(overridePosition);
    }

    const newOverridePositionsMap = overridePositionsMap.copy();

    for (const positionType of responsePositionTypes) {
      if (positionType === PositionTypeEnum.Menu){
        const dictionaryGroups = this.updateMenu(originalDictionaryGroups, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionaryGroupsAndOverridePositions([newOverridePositionsMap, dictionaryGroups]));
      }
      else if (positionType === PositionTypeEnum.Group || positionType === PositionTypeEnum.GroupStructure) {
        const dictionaryGroups = this.updateMenu(originalDictionaryGroups, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionaryGroupsAndOverridePositions([newOverridePositionsMap, dictionaryGroups]));
      }
      else if (positionType === PositionTypeEnum.Product ||
        positionType === PositionTypeEnum.ProductStructure ||
        positionType === PositionTypeEnum.ComboStructure ||
        positionType === PositionTypeEnum.SizeStructure ||
        positionType === PositionTypeEnum.ModifierGroupStructure ||
        positionType === PositionTypeEnum.ModifierProductStructure ||
        positionType === PositionTypeEnum.ComboProductStructure) {
        const dictionaryProducts = this.updateDictionaryProducts(originalDictionaryProducts, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionaryProductsAndOverridePositions([newOverridePositionsMap, dictionaryProducts]));
      } else if (positionType === PositionTypeEnum.Size) {
        const sizes = this.updateDictionarySizes(originalDictionarySizes, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionarySizesAndOverridePositions([newOverridePositionsMap, sizes]));
      } else if (positionType === PositionTypeEnum.ModifierGroup) {
        const dictionaryModifiersGroups = this.updateDictionaryModifierGroups(originalDictionaryModifierGroup, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionaryModifierGroupsAndOverridePositions([newOverridePositionsMap, dictionaryModifiersGroups]));
      } else if (positionType === PositionTypeEnum.ComboGroup) {
        const comboGroups = this.updateDictionaryComboGroups(originalDictionaryComboGroup, newOverridePositionsMap);
        this.store.dispatch(MenuActions.setDictionaryComboGroupsAndOverridePositions([newOverridePositionsMap, comboGroups]));
      } else {
        this.store.dispatch(MenuActions.setOverridePositions(newOverridePositionsMap));
      }
    }
  }

  public loadMenuSyncOfSales(organizationId: string){
    return this.dishesService.getMenuSyncsForSaleSystems(organizationId)
      .pipe(tap(menuSyncsOfSale =>
        {
          this.store.dispatch(setMenuSyncOfSales(menuSyncsOfSale))
        }
    ));
  }

  public loadMenuSyncOfPos(organizationId: string){
    return this.dishesService.getMenuSyncForExternalPOS(organizationId)
      .pipe(
        concatLatestFrom( () => this.store.select(selectMenuOrganization)),
        tap(([ menuSyncsOfPos, posOrganization]) =>
          {
            if (posOrganization?.importFromPos){
              this.store.dispatch(setMenuSyncOfPOS(menuSyncsOfPos));
            }
          }
        )
      );
  }

  public async createMenuSection(createMenuSectionRequest: CreateMenuSectionRequest): Promise<IBaseResponse<MenuSection>>{
    const response = await this.dishesService.createMenuSection(createMenuSectionRequest);
    if (!response.success){
      return response;
    }
    const menuSections = await firstValueFrom(this.store.select(selectMenuSections));
    const updatedMenuSections = [...menuSections, response.data];
    this.store.dispatch(MenuActions.setMenuSections(updatedMenuSections))
    return response;
  }

  public async updateMenuSection(updateMenuSectionRequest: UpdateMenuSectionRequest): Promise<IBaseResponse<MenuSection>>{
    const response = await this.dishesService.updateMenuSection(updateMenuSectionRequest);
    if (!response.success){
      return response;
    }
    const menuSections = await firstValueFrom(this.store.select(selectMenuSections));
    const updatedMenuSections = updateObject(menuSections, 'id', updateMenuSectionRequest);
    this.store.dispatch(MenuActions.setMenuSections(updatedMenuSections));
    return response;
  }

  public createGroup(createGroupRequest: CreateGroupRequest){
    return this.dishesService.createGroup(createGroupRequest)
      .pipe(
        concatLatestFrom(() => this.store.select(selectDictionaryGroupsAndOverridePositions)),
        tap(([response, {dictionaryGroups, overridePositionsMap}]) => {
          if (!response.success){
            return;
          }
          const newGroup: DisplayedGroup = {
            ...response.data,
            hasReOrderingProductInGroup: false,
            assortmentHasChanged:false,
            isFiltered: false,
            isChangedName: false,
            order: 0,
          };
          const newDictionaryGroups = new Map<string, DisplayedGroup>();
          for (const keyValue of dictionaryGroups.entries()) {
            newDictionaryGroups.set(keyValue[0], MenuOverrideService.overrideGroup(keyValue[1], 0, overridePositionsMap));
          }
          newDictionaryGroups.set(newGroup.externalId, newGroup);
          this.store.dispatch(MenuActions.setDictionaryGroups(newDictionaryGroups));
        })
      );
  }

  public getUpdateInformationMessage(changes: any[]) : string {
    let result = '';
    if (changes.length > 0) {
      result = `${this.translate.instant('MENU.CHANGES_FOR_MENUS', {'changes': changes.join(', ')})}`;
    }

    return result;
  }

  getSavedMenuSectionsIds(selectedMenuSections: object): string [] {
    const saveForMenuSections: string [] = [];
    for (const [key, value] of Object.entries(selectedMenuSections)) {
      if (value) {
        saveForMenuSections.push(key);
      }
    }
    return saveForMenuSections;
  }

  private updateMenu(dictionaryGroups: Map<string, Group>, newOverridePositionsMap: OverridePositionsMap): Map<string, DisplayedGroup>
  {
    return MenuOverrideService.overrideMenu(dictionaryGroups, newOverridePositionsMap);
  }

  private updateDictionaryProducts(dictionaryProducts: Map<string, Product>, newPositions: OverridePositionsMap): Map<string, DisplayedProduct>
  {
    const newDictionaryProducts = new Map<string, DisplayedProduct>();
    for (const keyValue of dictionaryProducts.entries()) {
      const originalProduct = keyValue[1];
      newDictionaryProducts.set(keyValue[0], MenuOverrideService.overrideProduct(originalProduct, newPositions));
    }
    return newDictionaryProducts;
  }

  private updateDictionarySizes(dictionarySizes: Map<string, Size>, newOverridePositionsMap: OverridePositionsMap): Map<string, DisplayedSize>
  {
    const newDictionarySizes = new Map<string, DisplayedSize>();
    for (const keyValue of dictionarySizes.entries()) {
      const newSize = JSON.parse(JSON.stringify(keyValue[1]));
      newDictionarySizes.set(keyValue[0], MenuOverrideService.overrideSize(newSize, newOverridePositionsMap));
    }
    return newDictionarySizes;
  }

  private updateDictionaryComboGroups(dictionaryComboGroups: Map<string, ComboGroup>, newOverridePositionsMap: OverridePositionsMap): Map<string, ComboGroup>
  {
    const newDictionaryComboGroups = new Map<string, ComboGroup>();
    for (const keyValue of dictionaryComboGroups.entries()) {
      const newComboGroup = JSON.parse(JSON.stringify(keyValue[1]));
      newDictionaryComboGroups.set(keyValue[0], MenuOverrideService.overrideComboGroup(newComboGroup, newOverridePositionsMap));
    }
    return newDictionaryComboGroups;
  }

  private updateDictionaryModifierGroups(dictionaryModifierGroups: Map<string, ModifierGroup>, newOverridePositionsMap: OverridePositionsMap): Map<string, ModifierGroup> {
    const newDictionaryModifierGroups = new Map<string, ModifierGroup>();
    for (const keyValue of dictionaryModifierGroups.entries()) {
      const newModiferGroup = JSON.parse(JSON.stringify(keyValue[1]));
      newDictionaryModifierGroups.set(keyValue[0], MenuOverrideService.overrideModifierGroup(newModiferGroup, newOverridePositionsMap));
    }
    return newDictionaryModifierGroups;
  }
}

